import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMqtt } from '@/contexts/mqtt-provider-context';

import type {
    VerticalScanConfig,
    VerticalScanUpdateSource,
    WinchLimits,
    WinchMqttUser,
    WinchStatusSnapshot,
    WinchTelemetry,
} from '../winchMqttApi';
import { isWinchMoving } from '../winchMqttApi';

import { cageWinchMqttReducer, initialCageWinchMqttState } from './cageWinchMqttState';
import { handleWinchMqttMessage, type PendingMeasurementSave } from './handleWinchMqttMessage';
import { useScanStartTracking } from './useScanStartTracking';
import { useSetLimitsAck } from './useSetLimitsAck';
import { useSetPositionAck } from './useSetPositionAck';
import { useWinchMqttCommands } from './useWinchMqttCommands';
import { useWinchMqttSubscription } from './useWinchMqttSubscription';

export type CageWinchMqtt = {
    online: boolean;
    telemetry: WinchTelemetry | null;
    config: WinchStatusSnapshot | null;
    limits: WinchLimits | null;
    calibrated: boolean;
    moving: boolean;
    canStop: boolean;
    canMove: boolean;
    setPositionPending: boolean;
    setPositionFailed: boolean;
    setLimitsPending: boolean;
    setLimitsFailed: boolean;
    verticalScan: VerticalScanConfig | null;
    verticalScanSource: VerticalScanUpdateSource | null;
    /** Start was sent and the device has not yet answered it or a later scan status request. */
    scanStarting: boolean;
    saveMeasurementPosition: (coords: { distance: number; depth: number }) => void;
    moveToMeasurement: () => void;
    moveToService: () => void;
    stop: () => void;
    disableVerticalScan: () => boolean;
    setVerticalScanLimits: (input: { x: number; yMin: number; yMax: number }) => boolean;
    enableVerticalScan: () => boolean;
    brokerConnected: boolean;
    awaitingLive: boolean;
};

export function useCageWinchMqtt(
    winchId: number | null,
    winchExists: boolean,
    user: WinchMqttUser
): CageWinchMqtt {
    const { t } = useTranslation();
    const { client, isConnected, subscribe, unsubscribe, publish } = useMqtt();
    const [state, dispatch] = useReducer(cageWinchMqttReducer, initialCageWinchMqttState);
    const stateRef = useRef(state);
    stateRef.current = state;
    const tRef = useRef(t);
    tRef.current = t;
    const winchIdRef = useRef(winchId);
    winchIdRef.current = winchId;
    const userRef = useRef(user);
    userRef.current = user;
    const previousWinchIdRef = useRef<number | null | undefined>(undefined);
    const pendingMeasurementRef = useRef<PendingMeasurementSave | null>(null);
    const {
        setPositionPending,
        setPositionFailed,
        beginSetPositionPending,
        settleSetPosition,
        resetSetPositionAck,
    } = useSetPositionAck({
        isConnected,
        tRef,
        pendingMeasurementRef,
    });
    const messageHandlerRef = useRef<(topic: string, message: Uint8Array) => void>(() => undefined);
    const heardRef = useRef(false);
    const stopSnapshotRetriesRef = useRef<() => void>(() => undefined);
    const [awaitingLive, setAwaitingLive] = useState(true);
    const markHeard = (): void => {
        if (heardRef.current) {
            return;
        }
        heardRef.current = true;
        stopSnapshotRetriesRef.current();
        setAwaitingLive(false);
    };
    const {
        setLimitsPending,
        setLimitsFailed,
        beginSetLimitsPending,
        settleSetLimits,
        failSetLimits,
        resetSetLimitsAck,
    } = useSetLimitsAck({
        isConnected,
        tRef,
    });

    const {
        scanStarting,
        publishScanStatusRef,
        onScanEnablePublished,
        onScanReply,
        resetScanStart,
    } = useScanStartTracking({ winchId, userRef, publish });

    messageHandlerRef.current = (topic: string, message: Uint8Array) => {
        handleWinchMqttMessage(
            {
                getWinchId: () => winchIdRef.current,
                getUser: () => userRef.current,
                getState: () => stateRef.current,
                dispatch: (action) => {
                    dispatch(action);
                    // Only getposition carries the saved presets Move relies on; keep
                    // retrying it even when telemetry or status already arrived.
                    if (action.type === 'SET_CONFIG' && action.replacePositions) {
                        markHeard();
                        return;
                    }
                    if (
                        action.type === 'SET_TELEMETRY' ||
                        action.type === 'SET_CONFIG' ||
                        (action.type === 'SET_ONLINE' && !action.payload)
                    ) {
                        setAwaitingLive(false);
                    }
                },
                publish,
                t: tRef.current,
                pendingMeasurementRef,
                onSetPositionSettled: settleSetPosition,
                onSetLimitsSettled: settleSetLimits,
                onScanReply,
                requestScanStatus: () => publishScanStatusRef.current(),
            },
            topic,
            message
        );
    };

    useEffect(() => {
        if (previousWinchIdRef.current === winchId) {
            return;
        }
        if (previousWinchIdRef.current !== undefined) {
            dispatch({ type: 'RESET' });
            heardRef.current = false;
            setAwaitingLive(true);
            resetScanStart();
            resetSetPositionAck();
            resetSetLimitsAck();
        }
        previousWinchIdRef.current = winchId;
    }, [resetScanStart, resetSetLimitsAck, resetSetPositionAck, winchId]);

    useWinchMqttSubscription({
        winchId,
        winchExists,
        client,
        isConnected,
        subscribe,
        unsubscribe,
        publish,
        userRef,
        messageHandlerRef,
        heardRef,
        stopSnapshotRetriesRef,
        publishScanStatusRef,
        setAwaitingLive,
    });

    const canPublish = isConnected && winchExists && winchId != null && Boolean(client);
    const commands = useWinchMqttCommands({
        canPublish,
        winchId,
        publish,
        userRef,
        pendingMeasurementRef,
        beginSetPositionPending,
        beginSetLimitsPending,
        failSetLimits,
        onScanEnablePublished,
    });

    const calibrated = state.telemetry?.calibrated ?? false;
    const motionState = state.telemetry?.state ?? null;
    const moving = isWinchMoving(motionState);
    const limits: WinchLimits | null = state.config?.limits ?? null;

    return {
        online: state.online,
        telemetry: state.telemetry,
        config: state.config,
        limits,
        calibrated,
        moving,
        canStop: isConnected && state.online && winchExists && moving,
        // Calibration is not a movement gate, and cloud telemetry carries no pitch/roll.
        // The device rejects unsafe moves itself.
        canMove: isConnected && state.online && winchExists && !moving,
        setPositionPending,
        setPositionFailed,
        setLimitsPending,
        setLimitsFailed,
        verticalScan: state.verticalScan,
        verticalScanSource: state.verticalScanSource,
        scanStarting,
        brokerConnected: isConnected && Boolean(client),
        awaitingLive,
        ...commands,
    };
}
